import Page from '../Components/SettingsPage';
import { useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import useSlackStatus from '../../../hooks/useSlackStatus';
import useCompleteSlackInstallationMutation from '../../../hooks/useCompleteSlackInstallationMutation';
import useRevokeSlackIntegrationMutation from '../../../hooks/useRevokeSlackIntegrationMutation';
import useChannels from '../../../hooks/useChannels';
import { useAuth } from '../../../hooks/useAuth';
import useAnalytics from '../../../hooks/useAnalytics';
import SlackInstallationBanner from './SlackInstallationBanner';
import SlackIntegrations from './SlackIntegrations';
import Skeleton from '@hud/web-components/Skeleton/Skeleton';
import ConfirmDisconnectDialog from './ConfirmDisconnectDialog';
import SetChannelDialogRenderer from './SetChannelDialogRenderer';
import config from '../../../config';
import ChannelsTable, {
  EditChannelClickedEventArgs,
  ConnectChannelsClickedEventArgs,
} from './ChannelsTable';
import { Channel, isSlackChannel } from '@hud/portal-dtos/channels.dtos';
import {
  SLACK_INTEGRATION_ERROR_CODES,
  SlackChannel,
  SlackIntegrationErrorCode,
} from '@hud/slack-dtos/api';
import useUpdateChannelsMutation from '../../../hooks/useUpdateChannels';
import useDeleteChannelMutation from '../../../hooks/useDeleteChannelMutation';

type DialogType = 'confirm' | 'setChannel' | null;

export default function SlackIntegrationPage() {
  const auth = useAuth();
  const analytics = useAnalytics();
  const { data: slackStatus, isLoading: isLoadingSlackStatus } = useSlackStatus();
  const { data: channelsData, isLoading: isLoadingChannels } = useChannels(
    slackStatus?.status === 'installed',
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const completeInstallation = useCompleteSlackInstallationMutation();
  const revokeIntegration = useRevokeSlackIntegrationMutation();
  const updateIntegrations = useUpdateChannelsMutation();
  const deleteChannel = useDeleteChannelMutation();
  const [openDialog, setOpenDialog] = useState<DialogType>(null);
  const [selectedServices, setSelectedServices] = useState<Channel[]>([]);

  const [editedChannel, setEditedChannel] = useState<Channel>();

  const slackIntegrationError = channelsData?.channels.find(
    (channel) =>
      isSlackChannel(channel) &&
      channel.errorMessage &&
      SLACK_INTEGRATION_ERROR_CODES.includes(
        channel.errorMessage as SlackIntegrationErrorCode,
      ),
  )?.errorMessage as SlackIntegrationErrorCode | undefined;

  useEffect(() => {
    const code = searchParams.get('code');
    if (code && auth.isAuthenticated) {
      completeInstallation.mutate(code);
      searchParams.delete('code');
      setSearchParams(searchParams);
    }
  }, [searchParams, setSearchParams, completeInstallation]);

  const handleDisconnect = () => {
    analytics.capture('disconnect_slack_clicked', {});
    setOpenDialog('confirm');
  };

  const confirmDisconnect = () => {
    revokeIntegration.mutate();
    setOpenDialog(null);
  };

  const handleSaveNotification = (selectedChannel: SlackChannel | null) => {
    if (!selectedChannel) {
      return;
    }

    analytics.capture('slack_channel_saved', {
      service_ids: selectedServices.map((s) => s.serviceId),
      channel_name: selectedChannel.name,
    });

    updateIntegrations.mutate({
      channels: selectedServices.map((service) => ({
        type: 'slack',
        serviceId: service.serviceId,
        config: {
          channelId: selectedChannel.id,
          channelName: selectedChannel.name,
        },
      })),
    });
    resetDialog();
  };

  const resetDialog = () => {
    setOpenDialog(null);
    setEditedChannel(undefined);
    setSelectedServices([]);
  };

  const redirectToSlackInstall = () => {
    const redirectUri = `${window.location.origin}/settings/slack`;

    // Use URLSearchParams to properly encode the OAuth URL
    const oauthParams = new URLSearchParams({
      scope: [
        'chat:write', // Post messages as the bot
        'channels:read', // List public channels the bot is in
        'groups:read', // List private channels the bot is in (optional for UI)
        'channels:history', // Read messages in public channels (for threads/replies)
        'groups:history', // Read messages in private channels (for threads/replies)
        'reactions:read', // Read reactions to messages
      ].join(','),
      user_scope: '',
      redirect_uri: redirectUri,
      client_id: config.SLACK.CLIENT_ID,
    });

    const oauthUrl = `https://slack.com/oauth/v2/authorize?${oauthParams.toString()}`;
    window.open(oauthUrl, '_self');
  };

  const handleInstallClick = () => {
    analytics.capture('connect_slack_clicked', {});
    redirectToSlackInstall();
  };

  const handleAddChannelClicked = (channel: Channel) => {
    analytics.capture('slack_channel_selection_click', {
      service_id: channel.serviceId,
    });
    setSelectedServices([channel]);
    setOpenDialog('setChannel');
  };

  const handleEditChannelClicked = (args: EditChannelClickedEventArgs) => {
    analytics.capture('slack_channel_selection_click', {
      service_id: args.channel.serviceId,
    });
    setEditedChannel(args.channel);
    setSelectedServices([args.channel]);
    setOpenDialog('setChannel');
  };

  const handleDeleteChannel = (channelId: string) => {
    // Find the channel being deleted to get its service ID
    const channelToDelete = channelsData?.channels.find(
      (ch) => ch.type !== 'missing' && ch.id === channelId,
    );
    if (channelToDelete) {
      analytics.capture('slack_channel_deleted', {
        service_id: channelToDelete.serviceId,
      });
    }
    deleteChannel.mutate(channelId);
    resetDialog();
  };

  const handleConnectChannelsClicked = (args: ConnectChannelsClickedEventArgs) => {
    analytics.capture('bulk_slack_channel_selection_click', {
      service_ids: args.selectedChannels.map((ch) => ch.serviceId),
    });
    if (args.selectedChannels.length === 1) {
      setEditedChannel(args.selectedChannels[0]);
    }
    setSelectedServices(args.selectedChannels);
    setOpenDialog('setChannel');
  };

  const handleReauthenticateClick = () => {
    analytics.capture('reauthenticate_slack_clicked', {});
    redirectToSlackInstall();
  };

  const isLoading = isLoadingSlackStatus || revokeIntegration.isPending;

  return (
    <Page header="Slack Integration">
      <h2>Slack Integration</h2>
      <ConfirmDisconnectDialog
        opened={openDialog === 'confirm'}
        onClose={() => setOpenDialog(null)}
        onConfirm={confirmDisconnect}
      />
      <SetChannelDialogRenderer
        opened={openDialog === 'setChannel'}
        onClose={resetDialog}
        onSave={handleSaveNotification}
        selectedServices={selectedServices}
        editedChannel={editedChannel ?? undefined}
        onDelete={handleDeleteChannel}
      />
      {isLoading ? (
        <Skeleton />
      ) : slackStatus?.status === 'not_installed' ? (
        <SlackInstallationBanner onInstallClick={handleInstallClick} />
      ) : (
        <>
          <SlackIntegrations
            onDisconnectClick={handleDisconnect}
            onReauthenticateClick={handleReauthenticateClick}
            integrationError={slackIntegrationError}
          />
          <ChannelsTable
            channels={channelsData?.channels || []}
            isLoading={isLoadingChannels}
            onAddChannelClicked={handleAddChannelClicked}
            onEditChannelClicked={handleEditChannelClicked}
            onConnectChannelsClicked={handleConnectChannelsClicked}
          />
        </>
      )}
    </Page>
  );
}
